import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { TrendingUp } from "lucide-react";

const COLORS = ["#0A1A4A", "#E63946", "#047857", "#D97706", "#7C3AED", "#0891B2", "#BE185D", "#4B5563"];
const fmtD = (d) => d.split("-").reverse().slice(0, 2).join("/");

export default function GradesChart({ grades }) {
  const subjects = useMemo(() => [...new Set(grades.map((g) => g.subject))], [grades]);
  const [active, setActive] = useState("all");

  const rows = useMemo(() => {
    const shown = active === "all" ? subjects : [active];
    const byDate = {};
    [...grades].sort((a, b) => a.date.localeCompare(b.date)).forEach((g) => {
      if (!shown.includes(g.subject)) return;
      byDate[g.date] = byDate[g.date] || { date: g.date, label: fmtD(g.date) };
      byDate[g.date][g.subject] = Math.round((g.score / g.max_score) * 200) / 10;
      byDate[g.date][`${g.subject}_title`] = g.title;
    });
    return Object.values(byDate);
  }, [grades, subjects, active]);

  const avgOf = (s) => { const xs = grades.filter((g) => g.subject === s); return xs.length ? (xs.reduce((a, g) => a + (g.score / g.max_score) * 20, 0) / xs.length).toFixed(1) : null; };
  const trendOf = (s) => { const xs = [...grades].filter((g) => g.subject === s).sort((a, b) => a.date.localeCompare(b.date)); if (xs.length < 2) return null; const n = (g) => (g.score / g.max_score) * 20; return n(xs[xs.length - 1]) - n(xs[0]); };

  if (grades.length < 2) return null;

  return (
    <div data-testid="grades-chart" className="bg-white border border-border rounded-sm p-5 mb-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2 mr-auto"><TrendingUp className="h-5 w-5 text-primary" /> Εξέλιξη βαθμών</h3>
        <button data-testid="grades-chart-all" onClick={() => setActive("all")} className={`h-8 px-3 rounded-sm border text-xs font-mono-accent uppercase tracking-widest ${active === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Όλα</button>
        {subjects.map((s, i) => <button key={s} data-testid={`grades-chart-${s}`} onClick={() => setActive(s)} className={`h-8 px-3 rounded-sm border text-xs inline-flex items-center gap-1.5 ${active === s ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s}</button>)}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 20]} ticks={[0, 5, 10, 15, 20]} tick={{ fontSize: 11 }} />
            <ReferenceLine y={10} stroke="#D97706" strokeDasharray="4 4" />
            <Tooltip formatter={(v, name, p) => [`${v}/20 — ${p.payload[`${name}_title`] || ""}`, name]} labelFormatter={(l) => `Ημ/νία: ${l}`} contentStyle={{ fontSize: 12, borderRadius: 2 }} />
            {active === "all" && subjects.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {(active === "all" ? subjects : [active]).map((s) => <Line key={s} type="monotone" dataKey={s} stroke={COLORS[subjects.indexOf(s) % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" data-testid="grades-chart-stats">
        {subjects.map((s, i) => { const t = trendOf(s); return (
          <div key={s} className="bg-secondary rounded-sm px-3 py-2">
            <div className="font-mono-accent text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s}</div>
            <div className="font-heading font-bold text-lg">{avgOf(s)}<span className="text-xs text-muted-foreground">/20</span>{t != null && <span className={`ml-2 text-xs font-mono-accent ${t > 0 ? "text-emerald-700" : t < 0 ? "text-red-700" : "text-muted-foreground"}`}>{t > 0 ? "▲" : t < 0 ? "▼" : "="} {Math.abs(t).toFixed(1)}</span>}</div>
          </div>); })}
      </div>
    </div>
  );
}
